import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  PhoneWorkflowEvent,
  PhoneWorkflowProgress,
  PhoneWorkflowAttemptState,
  PhoneWorkflowProviderOrderEntry,
} from '@/lib/types/phone-workflow.types';
import type { PhoneWorkflowSnapshot } from '@/store/api/phoneWorkflowApi';

/**
 * Live phone workflow progress, pushed by the Workflow service over the
 * app-wide Notification-Service SSE stream (see sidebar.tsx). Keyed by
 * workflowId. Kept entirely separate from workflowProgressSlice.ts (email) -
 * see phone-workflow.types.ts for why.
 */
export interface PhoneWorkflowProgressState {
  byWorkflowId: Record<string, PhoneWorkflowProgress>;
}

const initialState: PhoneWorkflowProgressState = { byWorkflowId: {} };

function upsertAttempt(
  attempts: Record<number, PhoneWorkflowAttemptState>,
  rank: number,
  update: Partial<PhoneWorkflowAttemptState>
): Record<number, PhoneWorkflowAttemptState> {
  const existing = attempts[rank] ?? { rank };
  return { ...attempts, [rank]: { ...existing, ...update } };
}

const phoneWorkflowProgressSlice = createSlice({
  name: 'phoneWorkflowProgress',
  initialState,
  reducers: {
    /**
     * Initialise a workflow entry when a POST /api/phone-workflows response
     * arrives (before any SSE events have been received).
     */
    phoneWorkflowInitialized(
      state,
      action: PayloadAction<{
        workflowId: string;
        personId: string;
        companyId: string;
        /** From the create response - the waterfall's planned provider order, the only list the modal can show pending rows from. */
        providerOrder?: PhoneWorkflowProviderOrderEntry[];
      }>
    ) {
      const { workflowId, personId, companyId, providerOrder } = action.payload;
      if (!state.byWorkflowId[workflowId]) {
        state.byWorkflowId[workflowId] = {
          workflowId,
          personId,
          companyId,
          providerOrder: providerOrder ?? [],
          currentAttemptRank: 0,
          // The waterfall always starts at rank 0, and that provider is already
          // known from this same create response - no need to wait for its
          // provider.searching SSE event just to learn who's being tried first.
          currentProviderName: providerOrder?.[0]?.providerName,
          currentProviderKey: providerOrder?.[0]?.providerKey,
          phone: null,
          completedAt: null,
          attempts: {},
        };
      } else if (providerOrder?.length) {
        // An SSE event can beat the POST response (see the skeleton in
        // phoneWorkflowEventReceived, which has no provider order to work
        // from) - fill it in rather than leaving the modal with an empty list.
        const wf = state.byWorkflowId[workflowId];
        wf.providerOrder = providerOrder;
        if (wf.currentProviderName === undefined && wf.currentAttemptRank === 0 && wf.completedAt === null) {
          wf.currentProviderName = providerOrder[0]?.providerName;
          wf.currentProviderKey = providerOrder[0]?.providerKey;
        }
      }
    },

    /** Apply a single workflow.progress SSE event (category "phone") to the progress state. */
    phoneWorkflowEventReceived(state, action: PayloadAction<PhoneWorkflowEvent>) {
      const evt = action.payload;
      let wf = state.byWorkflowId[evt.workflowId];
      if (!wf) {
        // May arrive before the init - bootstrap a skeleton.
        wf = {
          workflowId: evt.workflowId,
          personId: '',
          companyId: '',
          // Unknown until the POST response lands (phoneWorkflowInitialized backfills it).
          providerOrder: [],
          currentAttemptRank: evt.rank ?? 0,
          currentProviderName: undefined,
          phone: null,
          completedAt: null,
          attempts: {},
        };
        state.byWorkflowId[evt.workflowId] = wf;
      }

      switch (evt.event) {
        case 'workflow.started':
          // Nothing to update beyond the skeleton above - the first real
          // progress signal is provider.searching.
          break;

        case 'provider.searching':
          // providerKey is what the modal matches on to decide which row
          // animates - set it even when `rank` is absent, so a payload without
          // one still moves the spinner onto the right provider.
          wf.currentProviderName = evt.providerName;
          wf.currentProviderKey = evt.providerKey;
          if (evt.rank != null) {
            wf.currentAttemptRank = evt.rank;
            wf.attempts = upsertAttempt(wf.attempts, evt.rank, {
              rank: evt.rank,
              providerKey: evt.providerKey,
              providerName: evt.providerName,
              dispatchState: 'dispatched',
            });
          }
          break;

        case 'workflow.finished': {
          wf.completedAt = evt.at;
          wf.phone = evt.phone ?? null;
          // Kept on the run, not an attempt: this event carries no rank or
          // providerKey to attribute it with. A run that stopped on an empty
          // balance has phone: null just like a genuine miss - this is the only
          // thing telling them apart (see useContactUnlock.isFieldCreditExhausted).
          if (evt.status === 'found' || evt.status === 'not_found' || evt.status === 'credit_exhausted') {
            wf.finishedStatus = evt.status;
          }
          wf.currentProviderName = undefined;
          wf.currentProviderKey = undefined;
          // Same attribution fix as workflowProgressSlice's workflow.finished -
          // evt.rank is the waterfall's rank for THIS event, not necessarily
          // the rank of whichever provider's number is being reported. Prefer
          // an exact providerKey match when the event carries one.
          const matchedRank = evt.providerKey
            ? Object.entries(wf.attempts).find(([, a]) => a?.providerKey === evt.providerKey)?.[0]
            : undefined;
          const rank = matchedRank != null ? Number(matchedRank) : evt.rank;
          if (rank != null) {
            wf.attempts = upsertAttempt(wf.attempts, rank, {
              dispatchState: 'resolved',
              phone: evt.phone ?? null,
            });
          }
          break;
        }
      }
    },

    /** Remove a workflow's progress (e.g. after it's been handled). */
    phoneWorkflowProgressRemoved(state, action: PayloadAction<string>) {
      delete state.byWorkflowId[action.payload];
    },

    /**
     * Rebuilds one workflow's progress from a fresh GET
     * /phone-workflows/:id snapshot, overwriting whatever was there. Same
     * self-healing purpose as workflowResynced in workflowProgressSlice.ts -
     * dispatched on stream reconnect for every phone workflow still in
     * progress (see sidebar.tsx).
     */
    phoneWorkflowResynced(state, action: PayloadAction<PhoneWorkflowSnapshot>) {
      const snapshot = action.payload;
      const attemptsByRank: Record<number, PhoneWorkflowAttemptState> = {};
      for (const attempt of snapshot.attempts) {
        attemptsByRank[attempt.rank] = {
          rank: attempt.rank,
          providerKey: attempt.providerKey,
          providerName: attempt.providerName,
          creditCost: attempt.creditCost,
          dispatchState: attempt.dispatchState,
          status: attempt.status,
          phone: attempt.phone,
        };
      }

      const currentAttempt = attemptsByRank[snapshot.currentRank];

      state.byWorkflowId[snapshot._id] = {
        workflowId: snapshot._id,
        personId: snapshot.personId,
        companyId: snapshot.companyId,
        providerOrder: snapshot.providerOrder ?? [],
        currentAttemptRank: snapshot.currentRank,
        currentProviderName:
          snapshot.completedAt == null && currentAttempt?.dispatchState !== 'resolved'
            ? currentAttempt?.providerName
            : undefined,
        currentProviderKey:
          snapshot.completedAt == null && currentAttempt?.dispatchState !== 'resolved'
            ? currentAttempt?.providerKey
            : undefined,
        phone: snapshot.phone,
        completedAt: snapshot.completedAt,
        attempts: attemptsByRank,
      };
    },
  },
});

export const {
  phoneWorkflowInitialized,
  phoneWorkflowEventReceived,
  phoneWorkflowProgressRemoved,
  phoneWorkflowResynced,
} = phoneWorkflowProgressSlice.actions;

export default phoneWorkflowProgressSlice.reducer;
